/**
 * CONVERSATION PAGE
 *
 * Full-featured chat interface for a single conversation between two users.
 *
 * Layout:
 * - Desktop: Side-by-side layout (pinned items on left, messages on right)
 * - Mobile: Collapsible items panel above scrolling messages
 * - Auto-scrolls to newest message on load and when new messages arrive
 *
 * Key Features:
 * - Real-time message updates using Firestore subscriptions
 * - Pinned items/creations displayed prominently (items being discussed)
 * - Click pinned items to view full details in modal
 * - Sellers can update item status from conversation (mark as sold, pending, etc.)
 * - Users can add more items mid-conversation
 * - Pop-out button to open chat in a separate window
 * - Message types: text, inquiry, item_added, status_update, item_removed
 * - Mark messages as read automatically when viewing conversation
 *
 * Message Flow:
 * 1. User types message and hits send
 * 2. sendMessage called from messagingService
 * 3. Message added to conversations/:id/messages subcollection
 * 4. Conversation's lastMessage field updated
 * 5. Real-time listener receives update and displays new message
 *
 * Pinned Items System:
 * - Items being discussed are "pinned" to the conversation
 * - Displayed in left sidebar (desktop) or collapsible panel (mobile)
 * - Each pinned item shows photo, name, brand, and current status
 * - Clicking opens marketplace detail modal with full info
 * - Seller can remove items or mark them as sold/pending
 *
 * Technical Notes:
 * - Uses onSnapshot for real-time conversation and message updates
 * - Combines data from conversations collection and messages subcollection
 * - Handles both CollectionItems and Creations as pinned items
 * - Implements message read tracking with lastReadAt timestamps
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { subscribeToMessages, sendMessage, markMessagesAsRead, deleteEmptyConversation } from '../messaging/messagingService';
import type { Message, Conversation as ConversationType, ParticipantInfo, AttachedItem, AttachedCreation } from '../messaging/types';
import type { CollectionItem } from '../types';
import type { Creation } from '../types/creationTypes';
import MessageBubble from '../messaging/components/MessageBubble';
import PinnedItemWithStatus from '../messaging/components/PinnedItemWithStatus';
import PinnedCreationWithStatus from '../messaging/components/PinnedCreationWithStatus';
import AddItemsModal from '../messaging/components/AddItemsModal';
import MarketplaceItemDetailModal from '../components/marketplace/MarketplaceItemDetailModal';
import MarketplaceCreationDetailModal from '../components/marketplace/MarketplaceCreationDetailModal';
import { ArrowLeft, Send, Loader2, ChevronDown, ChevronUp, Plus, ExternalLink, Trash2 } from 'lucide-react';
import { showError } from '../utils/toast';

// Extended types to match marketplace modals
interface MarketplaceItem extends CollectionItem {
  sellerId: string;
  sellerUsername?: string;
}

interface MarketplaceCreation extends Creation {
  sellerId: string;
  sellerUsername?: string;
}

const Conversation: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<ConversationType | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPinnedItems, setShowPinnedItems] = useState(true);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [otherUser, setOtherUser] = useState<(ParticipantInfo & { id: string }) | null>(null);
  // Live-track the other party's moderationState — if they're banned we
  // swap the composer for an "unavailable" banner. Matches the mobile
  // messages/[id].tsx behavior.
  const [otherBanned, setOtherBanned] = useState(false);

  // Item detail modal state
  const [selectedItem, setSelectedItem] = useState<MarketplaceItem | null>(null);
  const [loadingItem, setLoadingItem] = useState(false);
  
  // Creation detail modal state
  const [selectedCreation, setSelectedCreation] = useState<MarketplaceCreation | null>(null);
  const [loadingCreation, setLoadingCreation] = useState(false);

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to conversation document
  useEffect(() => {
    if (!conversationId || !currentUserId) return;

    const conversationRef = doc(db, 'conversations', conversationId);
    const unsubscribe = onSnapshot(conversationRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as ConversationType;
        setConversation({ ...data, id: snapshot.id });

        // Get other user's info
        const otherUserId = data.participants.find(id => id !== currentUserId);
        if (otherUserId && data.participantInfo[otherUserId]) {
          setOtherUser({
            id: otherUserId,
            ...data.participantInfo[otherUserId]
          });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [conversationId, currentUserId]);

  // Subscribe to the other party's moderationState. Realtime so a
  // mid-conversation ban disables the composer without a reload.
  // Fail-open (banned=false) on read errors so a hiccup doesn't lock
  // a legitimate conversation.
  useEffect(() => {
    if (!otherUser?.id) return;
    const ref = doc(db, 'users', otherUser.id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const state = (snap.data() as { moderationState?: string } | undefined)?.moderationState;
        setOtherBanned(state === 'banned');
      },
      () => setOtherBanned(false),
    );
    return unsub;
  }, [otherUser?.id]);

  // Subscribe to messages
  useEffect(() => {
    if (!conversationId) return;

    const unsubscribe = subscribeToMessages(conversationId, (msgs) => {
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [conversationId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark messages as read when viewing conversation
  useEffect(() => {
    if (conversationId && currentUserId) {
      markMessagesAsRead(conversationId, currentUserId);
    }
  }, [conversationId, currentUserId, messages]);

  // Handle viewing item details - fetch full item data and open modal
  const handleViewItemDetails = async (attachedItem: AttachedItem) => {
    setLoadingItem(true);
    try {
      const itemRef = doc(db, 'users', attachedItem.ownerId, 'items', attachedItem.itemId);
      const itemSnap = await getDoc(itemRef);
      
      if (itemSnap.exists()) {
        const itemData = itemSnap.data() as CollectionItem;
        
        // Get seller username
        const userRef = doc(db, 'users', attachedItem.ownerId);
        const userSnap = await getDoc(userRef);
        const sellerUsername = userSnap.exists() ? userSnap.data()?.username : undefined;
        
        setSelectedItem({
          ...itemData,
          id: itemSnap.id,
          sellerId: attachedItem.ownerId,
          sellerUsername
        });
      } else {
        alert('Item not found. It may have been deleted.');
      }
    } catch (err) {
      console.error('Error fetching item details:', err);
      alert('Failed to load item details.');
    } finally {
      setLoadingItem(false);
    }
  };

  // Handle viewing creation details - fetch full creation data and open modal
  const handleViewCreationDetails = async (attachedCreation: AttachedCreation) => {
    setLoadingCreation(true);
    try {
      const creationRef = doc(db, 'users', attachedCreation.ownerId, 'creations', attachedCreation.creationId);
      const creationSnap = await getDoc(creationRef);
      
      if (creationSnap.exists()) {
        const creationData = creationSnap.data() as Creation;
        
        // Get seller username
        const userRef = doc(db, 'users', attachedCreation.ownerId);
        const userSnap = await getDoc(userRef);
        const sellerUsername = userSnap.exists() ? userSnap.data()?.username : undefined;
        
        setSelectedCreation({
          ...creationData,
          id: creationSnap.id,
          sellerId: attachedCreation.ownerId,
          sellerUsername
        });
      } else {
        alert('Creation not found. It may have been deleted.');
      }
    } catch (err) {
      console.error('Error fetching creation details:', err);
      alert('Failed to load creation details.');
    } finally {
      setLoadingCreation(false);
    }
  };

  // Send message handler
  const handleSend = async () => {
    if (!newMessage.trim() || !currentUserId || !conversationId || sending) return;

    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      await sendMessage(conversationId, {
        senderId: currentUserId,
        type: 'text',
        text: messageText,
        readBy: [currentUserId],
      });
    } catch (err) {
      console.error('Error sending message:', err);
      setNewMessage(messageText); // Restore message on error
      showError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Handle enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Pop out to new window
  const handlePopOut = () => {
    const width = 400;
    const height = 600;
    const left = window.screen.width - width - 20;
    const top = 100;
    
    window.open(
      `/messages/${conversationId}/popout`,
      `chat_${conversationId}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  // Get existing item/creation IDs to exclude from add modal
  const existingItemIds = conversation?.pinnedItems?.map(item => item.itemId) || [];
  const existingCreationIds = conversation?.pinnedCreations?.map(c => c.creationId) || [];
  
  // Total pinned count
  const pinnedItemCount = (conversation?.pinnedItems?.length || 0) + (conversation?.pinnedCreations?.length || 0);

  // Loading state
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4">
        <div className="glass-card p-12 text-center">
          <Loader2 className="w-12 h-12 text-glass animate-spin mx-auto mb-4" />
          <p className="text-glass-secondary font-body">Loading conversation...</p>
        </div>
      </div>
    );
  }

  // Conversation not found or unauthorized
  if (!conversation || !currentUserId) {
    return (
      <div className="max-w-6xl mx-auto px-4">
        <div className="glass-card p-12 text-center">
          <h2 className="text-xl text-glass mb-2 font-heading">Conversation not found</h2>
          <p className="text-glass-secondary mb-4 font-body">This conversation doesn't exist or you don't have access to it.</p>
          <Link 
            to="/messages"
            className="inline-block btn-primary font-body"
          >
            Back to Messages
          </Link>
        </div>
      </div>
    );
  }

  // Items panel content (used in both mobile and desktop)
  const ItemsPanel = ({ className = '' }: { className?: string }) => (
    <div className={className}>
      {/* Loading overlay when fetching item details */}
      {(loadingItem || loadingCreation) && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[200]">
          <div className="bg-white rounded-lg p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-glass animate-spin" />
            <span className="font-body">Loading details...</span>
          </div>
        </div>
      )}
      
      {/* Items grid */}
      <div className="flex flex-wrap gap-3 items-start">
        {/* Pinned Items */}
        {conversation.pinnedItems?.map((item, index) => (
          <PinnedItemWithStatus
            key={`item-${item.itemId}-${index}`}
            item={item}
            conversationId={conversationId!}
            currentUserId={currentUserId}
            isOwner={item.ownerId === currentUserId}
            onViewDetails={handleViewItemDetails}
          />
        ))}

        {/* Pinned Creations */}
        {conversation.pinnedCreations?.map((creation, index) => (
          <PinnedCreationWithStatus
            key={`creation-${creation.creationId}-${index}`}
            creation={creation}
            conversationId={conversationId!}
            currentUserId={currentUserId}
            isOwner={creation.ownerId === currentUserId}
            onViewDetails={handleViewCreationDetails}
          />
        ))}
        
        {/* Add Items Button */}
        <button
          onClick={() => setShowAddItemsModal(true)}
          className="flex flex-col items-center justify-center glass-stats rounded-lg border-2 border-dashed border-slate-600 hover:border-slate-500 hover:bg-slate-700/50 transition-default cursor-pointer"
          style={{ width: '140px', height: '152px' }}
        >
          <div className="w-12 h-12 rounded-full bg-slate-800/60 flex items-center justify-center mb-2">
            <Plus className="icon-base text-glass-muted" />
          </div>
          <span className="text-sm text-glass-muted font-bold font-body">
            Add Items
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* DESKTOP LAYOUT (lg and up): Side-by-side */}
      <div className="hidden lg:flex gap-4 h-[calc(100vh-180px)]">
        
        {/* Left Panel - Items */}
        <div className="w-80 flex-shrink-0 glass-card flex flex-col">
          {/* Items Header */}
          <div className="p-4 border-b border-slate-600">
            <h3 className="font-bold text-glass flex items-center gap-2 font-body">
              📌 Items in Discussion
              <span className="text-sm font-normal text-glass-muted">({pinnedItemCount})</span>
            </h3>
          </div>
          
          {/* Items List */}
          <div className="flex-1 overflow-y-auto p-4">
            {pinnedItemCount === 0 ? (
              <div className="text-center py-8">
                <p className="text-glass-muted text-sm mb-4 font-body">
                  No items yet
                </p>
                <button
                  onClick={() => setShowAddItemsModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg font-bold hover:bg-slate-800 transition-default text-sm font-body"
                >
                  <Plus className="icon-sm" />
                  Add Items
                </button>
              </div>
            ) : (
              <ItemsPanel />
            )}
          </div>
        </div>

        {/* Right Panel - Chat */}
        <div className="flex-1 glass-card flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-slate-600 flex items-center gap-4">
            {/* Back button */}
            <button
              onClick={() => navigate('/messages')}
              className="p-2 hover:bg-slate-800/60 rounded-full transition-default text-glass-secondary"
            >
              <ArrowLeft className="icon-md" />
            </button>

            {/* Other user info */}
            {otherUser && (
              <Link 
                to={`/profile/${otherUser.username || otherUser.id}`}
                className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
              >
                {otherUser.avatarUrl ? (
                  <img 
                    src={otherUser.avatarUrl} 
                    alt={otherUser.displayName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full rainbow-gradient flex items-center justify-center text-white font-bold">
                    {otherUser.displayName?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <h2 className="font-bold text-glass font-body">
                    {otherUser.username ? `@${otherUser.username}` : otherUser.displayName}
                  </h2>
                  <p className="text-xs text-glass-muted font-body">
                    Click to view profile
                  </p>
                </div>
              </Link>
            )}

            {/* Delete conversation — only shown when every message in
                the conversation is a known system type. Missing/unknown
                types are treated as user content and block deletion,
                mirroring the service function's server-side block-list so
                the two agree on eligibility. */}
            {conversationId && currentUserId &&
              messages.length > 0 &&
              messages.every((m) => {
                const t = m.type;
                return t === 'item_added' || t === 'creation_added' ||
                  t === 'status_update' || t === 'item_removed' ||
                  t === 'creation_removed';
              }) && (
                <button
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Delete this conversation with ${
                          otherUser?.username
                            ? `@${otherUser.username}`
                            : otherUser?.displayName ?? 'this user'
                        }? It has no messages you typed.`
                      )
                    ) {
                      return;
                    }
                    deleteEmptyConversation(conversationId, currentUserId)
                      .then(() => navigate('/messages'))
                      .catch((err) => {
                        console.error('delete conversation failed', err);
                        window.alert(err?.message ?? "Couldn't delete this conversation.");
                      });
                  }}
                  className="p-2 hover:bg-red-500/10 rounded-full transition-default text-glass-secondary hover:text-red-400"
                  title="Delete this conversation"
                  aria-label="Delete this conversation"
                >
                  <Trash2 className="icon-md" />
                </button>
              )}

            {/* Pop-out button */}
            <button
              onClick={handlePopOut}
              className="p-2 hover:bg-slate-800/60 rounded-full transition-default text-glass-secondary"
              title="Open in pop-out window"
            >
              <ExternalLink className="icon-md" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-800/20">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-glass-muted font-body">
                  No messages yet. Say hi! 👋
                </p>
              </div>
            ) : (
              messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwnMessage={message.senderId === currentUserId}
                  senderName={
                    message.senderId === currentUserId 
                      ? 'You' 
                      : otherUser?.displayName || 'Unknown'
                  }
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input — replaced with an "unavailable" banner when the
              other party has been banned. History above stays visible
              so the reporter can scroll back, but they can't compose.
              Wording is deliberately vague (matches profile stub) to
              avoid confirming the ban explicitly. */}
          {otherBanned ? (
            <div className="p-4 border-t border-slate-600 text-center">
              <p className="font-body text-glass-secondary text-sm">
                This account is no longer available.
              </p>
              <p className="font-body text-glass-muted text-xs mt-1">
                You cannot send new messages.
              </p>
            </div>
          ) : (
          <div className="p-4 border-t border-slate-600">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 border border-slate-600 rounded-full focus:ring-2 focus:ring-slate-500 focus:border-slate-600 bg-white/80 font-body"
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                className={`p-3 rounded-full transition-default ${
                  newMessage.trim() && !sending
                    ? 'btn-primary'
                    : 'bg-gray-200 text-glass-muted cursor-not-allowed'
                }`}
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="icon-md" />
                )}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* MOBILE LAYOUT (below lg): Stacked with collapsible items */}
      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col h-[calc(100dvh-120px)]">
        {/* Header */}
        <div className="glass-chat-header p-4 flex items-center gap-4 flex-shrink-0">
          {/* Back button */}
          <button
            onClick={() => navigate('/messages')}
            className="p-2 hover:bg-slate-800/60 rounded-full transition-default text-glass-secondary"
          >
            <ArrowLeft className="icon-md" />
          </button>

          {/* Other user info */}
          {otherUser && (
            <Link 
              to={`/profile/${otherUser.username || otherUser.id}`}
              className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
            >
              {otherUser.avatarUrl ? (
                <img 
                  src={otherUser.avatarUrl} 
                  alt={otherUser.displayName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full rainbow-gradient flex items-center justify-center text-white font-bold">
                  {otherUser.displayName?.charAt(0).toUpperCase() || '?'}
                </div>
              )}
              <div>
                <h2 className="font-bold text-glass font-body">
                  {otherUser.username ? `@${otherUser.username}` : otherUser.displayName}
                </h2>
                <p className="text-xs text-glass-muted font-body">
                  Tap to view profile
                </p>
              </div>
            </Link>
          )}

          {/* Pop-out button */}
          <button
            onClick={handlePopOut}
            className="p-2 hover:bg-slate-800/60 rounded-full transition-default text-glass-secondary"
            title="Open in pop-out window"
          >
            <ExternalLink className="icon-md" />
          </button>
        </div>

        {/* Pinned Items & Creations - Collapsible */}
        {pinnedItemCount > 0 && (
          <div className="bg-slate-800/40 backdrop-blur-sm border-b border-slate-600 px-4 py-2 flex-shrink-0">
            <button 
              onClick={() => setShowPinnedItems(!showPinnedItems)}
              className="flex items-center justify-between w-full text-sm text-glass-secondary font-bold font-body"
            >
              <span>📌 {pinnedItemCount} item{pinnedItemCount !== 1 ? 's' : ''} in discussion</span>
              {showPinnedItems ? <ChevronUp className="icon-sm" /> : <ChevronDown className="icon-sm" />}
            </button>
            
            {showPinnedItems && (
              <div className="mt-3 pb-2 max-h-[35vh] overflow-y-auto">
                <ItemsPanel />
              </div>
            )}
          </div>
        )}

        {/* Show Add Items button even if no pinned items yet */}
        {pinnedItemCount === 0 && (
          <div className="bg-slate-800/40 backdrop-blur-sm border-b border-slate-600 px-4 py-3 flex-shrink-0">
            <button
              onClick={() => setShowAddItemsModal(true)}
              className="flex items-center gap-2 text-sm text-glass font-bold hover:text-glass-secondary transition-default font-body"
            >
              <Plus className="icon-sm" />
              Add items to discuss
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 bg-slate-800/30 backdrop-blur-sm overflow-y-auto px-4 py-4 min-h-0">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-glass-muted font-body">
                No messages yet. Say hi! 👋
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwnMessage={message.senderId === currentUserId}
                senderName={
                  message.senderId === currentUserId 
                    ? 'You' 
                    : otherUser?.displayName || 'Unknown'
                }
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input — same ban-guard as the desktop layout above. */}
        {otherBanned ? (
          <div className="glass-chat-footer p-4 text-center flex-shrink-0">
            <p className="font-body text-glass-secondary text-sm">
              This account is no longer available.
            </p>
            <p className="font-body text-glass-muted text-xs mt-1">
              You cannot send new messages.
            </p>
          </div>
        ) : (
          <div className="glass-chat-footer p-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2 border border-slate-600 rounded-full focus:ring-2 focus:ring-slate-500 focus:border-slate-600 bg-white/80 font-body"
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                className={`p-3 rounded-full transition-default ${
                  newMessage.trim() && !sending
                    ? 'btn-primary'
                    : 'bg-gray-200 text-glass-muted cursor-not-allowed'
                }`}
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="icon-md" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Items Modal */}
      {otherUser && (
        <AddItemsModal
          isOpen={showAddItemsModal}
          onClose={() => setShowAddItemsModal(false)}
          conversationId={conversationId!}
          currentUserId={currentUserId}
          otherUserId={otherUser.id}
          existingItemIds={existingItemIds}
          existingCreationIds={existingCreationIds}
          onItemsAdded={() => {
            // Modal will close itself, items update via realtime listener
          }}
        />
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <MarketplaceItemDetailModal
          item={selectedItem}
          items={[selectedItem]} // Single item, no navigation needed
          currentIndex={0}
          currentUserId={currentUserId}
          onClose={() => setSelectedItem(null)}
          onNavigate={() => {}} // No navigation in single-item view
          onCopySuccess={() => {}}
        />
      )}

      {/* Creation Detail Modal */}
      {selectedCreation && (
        <MarketplaceCreationDetailModal
          creation={selectedCreation}
          creations={[selectedCreation]} // Single creation, no navigation needed
          currentIndex={0}
          currentUserId={currentUserId}
          onClose={() => setSelectedCreation(null)}
          onNavigate={() => {}} // No navigation in single-item view
        />
      )}
    </div>
  );
};

export default Conversation;
